import React, { useState, useEffect } from 'react';
import './FilterSidebar.css';
import { FiX } from 'react-icons/fi';

function FilterSidebar({ onApplyFilters, isOpen, onClose }) {
  const [startEnabled, setStartEnabled] = useState(false);
  const [targetEnabled, setTargetEnabled] = useState(false);
  const [startSearch, setStartSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [startSelected, setStartSelected] = useState([]);
  const [targetSelected, setTargetSelected] = useState([]);
  const [locations, setLocations] = useState([]);

  // New time filter states
  const [timeEnabled, setTimeEnabled] = useState(false);
  const [timeFilterType, setTimeFilterType] = useState('monat');
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });

  // Array of months for the month selector
  const months = [
    'Jänner', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];

  useEffect(() => {
    // Get locations from localStorage
    const stationsMap = JSON.parse(localStorage.getItem('stationsMap')) || {};
    const locationsList = Object.entries(stationsMap).map(([id, name]) => ({
      id: parseInt(id),
      name
    }));
    setLocations(locationsList);
  }, []);

  const getFilteredLocations = (searchTerm) => {
    if (!searchTerm) return locations;
    return locations.filter(loc => 
      loc.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleToggleLocation = (list, item, setList) => {
    const exists = list.find(i => i.id === item.id);
    if (exists) {
      setList(list.filter(i => i.id !== item.id));
    } else {
      setList([...list, item]);
    }
  };

  const handleMonthToggle = (monthIndex) => {
    if (selectedMonths.includes(monthIndex)) {
      setSelectedMonths(selectedMonths.filter(m => m !== monthIndex));
    } else {
      setSelectedMonths([...selectedMonths, monthIndex]);
    }
  };

  const handleApplyFilters = () => {
    const filters = {
      start: startEnabled ? startSelected.map(s => s.id) : null,
      target: targetEnabled ? targetSelected.map(s => s.id) : null,
      time: timeEnabled ? {
        type: timeFilterType,
        value: timeFilterType === 'monat' 
          ? selectedMonths  // This will be array of month indices (0-11)
          : {
              from: dateRange.from,  // ISO date string YYYY-MM-DD
              to: dateRange.to       // ISO date string YYYY-MM-DD
            }
      } : null
    };
    onApplyFilters(filters);
  };

  const Switch = ({ checked, onChange, label }) => (
    <label className="filter-toggle">
      <span>{label}</span>
      <label className="switch">
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
        />
        <span className="slider"></span>
      </label>
    </label>
  );

  return (
    <div className={`filter-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="filter-header">
        <h3>Filter</h3>
        <button className="close-button" onClick={onClose}>
          <FiX />
        </button>
      </div>
      
      <div className="filter-content">
        {/* Start Location Filter */}
        <div className="filter-section">
          <div className="filter-header">
            <Switch
              checked={startEnabled}
              onChange={(e) => setStartEnabled(e.target.checked)}
              label="Start Location"
            />
          </div>
          
          {startEnabled && (
            <>
              <input
                type="text"
                className="search-input"
                placeholder="Search start locations..."
                value={startSearch}
                onChange={(e) => setStartSearch(e.target.value)}
              />
              <div className="locations-list">
                {getFilteredLocations(startSearch).map(location => (
                  <div
                    key={location.id}
                    className={`location-item ${startSelected.find(s => s.id === location.id) ? 'selected' : ''}`}
                    onClick={() => handleToggleLocation(startSelected, location, setStartSelected)}
                  >
                    {location.name}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Target Location Filter */}
        <div className="filter-section">
          <div className="filter-header">
            <Switch
              checked={targetEnabled}
              onChange={(e) => setTargetEnabled(e.target.checked)}
              label="Target Location"
            />
          </div>
          
          {targetEnabled && (
            <>
              <input
                type="text"
                className="search-input"
                placeholder="Search target locations..."
                value={targetSearch}
                onChange={(e) => setTargetSearch(e.target.value)}
              />
              <div className="locations-list">
                {getFilteredLocations(targetSearch).map(location => (
                  <div
                    key={location.id}
                    className={`location-item ${targetSelected.find(s => s.id === location.id) ? 'selected' : ''}`}
                    onClick={() => handleToggleLocation(targetSelected, location, setTargetSelected)}
                  >
                    {location.name}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Time Filter */}
        <div className="filter-section">
          <div className="filter-header">
            <Switch
              checked={timeEnabled}
              onChange={(e) => setTimeEnabled(e.target.checked)}
              label="Zeit"
            />
          </div>
          
          {timeEnabled && (
            <div className="time-filter">
              <div className="radio-group">
                <label className={`radio-label ${timeFilterType === 'monat' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="timeFilter"
                    value="monat"
                    checked={timeFilterType === 'monat'}
                    onChange={(e) => setTimeFilterType(e.target.value)}
                  />
                  Monat
                </label>
                <label className={`radio-label ${timeFilterType === 'exaktes datum' ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="timeFilter"
                    value="exaktes datum"
                    checked={timeFilterType === 'exaktes datum'}
                    onChange={(e) => setTimeFilterType(e.target.value)}
                  />
                  Exaktes Datum
                </label>
              </div>

              {timeFilterType === 'monat' ? (
                <div className="months-select">
                  <select
                    multiple
                    value={selectedMonths}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                      setSelectedMonths(selected);
                    }}
                  >
                    {months.map((month, index) => (
                      <option key={month} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="date-range">
                  <div className="date-input">
                    <label>Von</label>
                    <input
                      type="date"
                      value={dateRange.from}
                      onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                    />
                  </div>
                  <div className="date-input">
                    <label>Bis</label>
                    <input
                      type="date"
                      value={dateRange.to}
                      onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <button 
        className="apply-button"
        onClick={handleApplyFilters}
      >
        Anwenden
      </button>
    </div>
  );
}

export default FilterSidebar; 